import { NBN_SESSION_KEYS } from '@belong/constants';
import { toSingleLineAddress } from '@belong/domain-address-capture';
import { setValuesToSessionStorage } from '@belong/session-storage';
import {
  IAddressDetail,
  IProactiveMas,
  IServiceQualificationAddress,
  IServiceQualificationResponse,
  ISqModel,
  ISqNbnProductModel
} from '@belong/types';
import { getServiceClass } from './serviceClass';

const SQ_KEYS = {
  DONE: NBN_SESSION_KEYS.SQ_DONE,
  MODEL: NBN_SESSION_KEYS.SQ_MODEL
} as const;

interface IServiceQueryData {
  storage: Storage;
  serviceabilityDetails: IServiceQualificationResponse;
  serviceAddress?: IServiceQualificationAddress | IAddressDetail;
  addressPID: string;
  numberOfAddressesFromPID: number;
  masReading?: IProactiveMas;
  isSqDone?: boolean;
}

export const setNbnProducts = ({
  products,
  alternateTechnologyProducts
}: IServiceQualificationResponse): ISqNbnProductModel => {
  const derivedProducts = products?.map(product => ({ ...product, isAlternateTechnology: false })) || [];
  const derivedAlternateTechnologyProducts =
    alternateTechnologyProducts?.map(product => ({ ...product, isAlternateTechnology: true })) || [];

  const nbnProducts = [...derivedProducts, ...derivedAlternateTechnologyProducts];
  setValuesToSessionStorage(NBN_SESSION_KEYS.PRODUCTS, nbnProducts);

  return nbnProducts;
};

export const setServiceQueryDataWithStrategicSQ = ({
  storage,
  serviceabilityDetails,
  serviceAddress,
  addressPID,
  masReading,
  isSqDone = true
}: IServiceQueryData): {
  address?: string;
  sqModel: ISqModel;
} => {
  const { serviceQualification, alternateTechnologyServiceQualification } = serviceabilityDetails;
  const nbnServiceClass = serviceQualification ? serviceQualification.nbnServiceClass : undefined;
  const nbnTechType = serviceQualification ? serviceQualification.nbnTechType : undefined;
  const sqLineStatus = serviceQualification ? serviceQualification.sqLineStatus : undefined;
  const newDevelopmentsCharge = (): boolean => {
    if (serviceQualification) {
      // yes -> is NDC -> return true
      // no -> not NDC -> return false
      // fallback -> not NDC -> false
      return serviceQualification.newDevelopmentsCharge !== 'No';
    }

    return false;
  };

  // Legacy support for V1 address structure (IServiceQualificationAddress)
  // Existence of 'localityName' is the safest way to check for legacy address format
  let sqModelAddress: Record<string, any> = {};
  if (serviceAddress && 'localityName' in serviceAddress) {
    sqModelAddress = {
      serviceStreetNumber: serviceAddress?.streetNumber,
      serviceStreetType: serviceAddress?.streetType,
      serviceStreet: serviceAddress?.streetName,
      serviceAddressId: serviceAddress?.addressId,
      serviceSuburb: serviceAddress?.localityName,
      serviceState: serviceAddress?.state,
      servicePostcode: serviceAddress?.postcode,
      serviceSubAddressDetails: serviceAddress?.subAddress
    };
  }

  const sqModel = {
    accessType: '',
    defaultSize: '',
    phoneNumber: '',
    serviceExchange: '',
    ...sqModelAddress,
    serviceAddress,
    isNbn: true,
    orderType: 'NEW_ORDER',
    promotionId: false,
    gnafpId: addressPID,
    numberOfAddressesFromVerifyAddress: 0,
    broadbandTechType: nbnTechType,
    extendedNetwork: false,
    ndc: newDevelopmentsCharge(),
    serviceClass: nbnServiceClass ? getServiceClass(Number(nbnServiceClass)) : '',
    inplaceType: sqLineStatus,
    migrationData: null,
    serviceAddressJwt: serviceabilityDetails.jwt,
    masReading: masReading || {},
    alternateTechnology: alternateTechnologyServiceQualification
  };

  // Save to sessionStorage
  storage.setItem(SQ_KEYS.DONE, isSqDone.toString());
  storage.setItem(SQ_KEYS.MODEL, JSON.stringify(sqModel));

  return {
    address: serviceAddress ? toSingleLineAddress(serviceAddress as IAddressDetail) : undefined,
    sqModel: sqModel as ISqModel
  };
};

export const hasSessionStorageSQData = (): boolean => {
  return Object.values(SQ_KEYS).every(key => !!sessionStorage.getItem(key));
};

export const ACTIVE_LINE_ON_ALTERNATE_TECH_ERROR_CODE = 'ACTIVE_LINE_FOUND_ON_ALTERNATE_TECH';
